import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePreFormWizard } from '@/hooks/submission_forms/usePreFormWizard';
import { useCategoryAutoSelect } from '@/hooks/submission_forms/useCategoryAutoSelect';
import { useSubCategoryAutoSelect } from '@/hooks/submission_forms/useSubCategoryAutoSelect';
import FullPageHeader from '@/components/FullPageHeader';
import LoadingState from '@/components/wizard/LoadingState';
import ErrorState from '@/components/wizard/ErrorState';
import WizardStep from '@/components/wizard/WizardStep';
import ReviewStep from '@/components/wizard/ReviewStep';
import { WizardStepType } from '@/components/wizard/types';

interface PreFormWizardWithSubCategoryProps {
  category: string;
  pageTitle: string;
  backPath: string;
}

// Locks both the category AND subcategory steps, deriving the subcategory from the
// study's own EAP status (owner.eap_status, computed server-side — see
// SubmissionPreFormBuilder#owner_info) rather than asking the user. Requires owner_id
// to be present in the query string; falls back to showing the subcategory step
// normally if the fetched owner has no eap_status (e.g. owner_id missing/not a Study).
const PreFormWizardWithSubCategory: React.FC<
  PreFormWizardWithSubCategoryProps
> = ({ category, pageTitle, backPath }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ownerId = searchParams.get('owner_id') ?? undefined;

  const {
    currentStepData,
    isLoading,
    error,
    selection,
    owner,
    blockedMessage,
    selectOption,
    previousStep,
    reset,
    initializeForm,
  } = usePreFormWizard();

  const {
    seeded: categorySeeded,
    categoryError,
    resetCategorySeed,
  } = useCategoryAutoSelect({
    category,
    isLoading,
    currentStepData,
    blockedMessage,
    selectOption,
  });

  const { subCategoryAutoSelected, resetSubCategorySeed } =
    useSubCategoryAutoSelect({
      categorySeeded,
      currentCategory: category,
      currentStepData,
      // This route only ever serves the site-creation category, so its derived
      // value is always the study's EAP status.
      targetCategory: category,
      derivedSubCategory: owner?.eap_status,
      // Irrelevant here (reArmOnCategoryChange is false), but the hook needs it.
      hasSubCategorySelection: (selection.subCategory ?? []).length > 0,
      selectOption,
      reArmOnCategoryChange: false,
    });

  useEffect(() => {
    initializeForm(ownerId);
  }, [initializeForm, ownerId]);

  const handleReset = () => {
    resetCategorySeed();
    resetSubCategorySeed();
    reset();
  };

  const handleClose = () => navigate(backPath);

  const handleBack = () => {
    // CATEGORY is always locked; SUBCATEGORY is only locked when it was actually
    // auto-selected — otherwise it's the step the user is meant to answer manually,
    // and Back should be able to land there normally.
    const lockedSteps = subCategoryAutoSelected
      ? [WizardStepType.CATEGORY, WizardStepType.SUBCATEGORY]
      : [WizardStepType.CATEGORY];

    // previousStep() itself knows whether an earlier, unlocked, non-empty step
    // actually exists to land on — e.g. for a category whose VARIANT has no
    // options at all (nothing to derive a variant from), CATEGORY, SUBCATEGORY,
    // AND VARIANT can all be simultaneously unreachable, and no hardcoded
    // "first visible step" guess can predict that. When it reports there's
    // nowhere left, leave the wizard the same way its own close button does —
    // browser-history back, not a hardcoded backPath, since that's not
    // necessarily where this session actually came from.
    if (!previousStep(lockedSteps)) {
      navigate(-1);
    }
  };

  const handleRetry = () => {
    resetCategorySeed();
    resetSubCategorySeed();
    reset();
    initializeForm(ownerId);
  };

  const hiddenSteps = subCategoryAutoSelected
    ? [WizardStepType.CATEGORY, WizardStepType.SUBCATEGORY]
    : [WizardStepType.CATEGORY];

  if (isLoading || !categorySeeded) {
    return <LoadingState message="Loading form options..." />;
  }

  // A blocked message means the owner is lifecycle-ineligible (e.g. not yet Active) —
  // retrying can't fix that, so send the user back instead of offering a false retry.
  if (categoryError && blockedMessage) {
    return (
      <ErrorState
        variant="info"
        message={categoryError}
        onBack={handleClose}
        actionLabel="Go Back"
      />
    );
  }

  if (error || categoryError) {
    return (
      <ErrorState message={categoryError ?? error!} onRetry={handleRetry} />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <FullPageHeader title={pageTitle} onClose={handleClose} />

      {/* Reserve the fixed header's height — see the note in PreFormWizard. */}
      <div className="flex-1 flex flex-col justify-center py-8 pt-[72px]">
        {currentStepData.currentStep === WizardStepType.REVIEW ? (
          <ReviewStep
            selection={selection}
            owner={owner}
            hiddenSteps={hiddenSteps}
            onBack={handleBack}
            onReset={handleReset}
          />
        ) : (
          <WizardStep
            currentStepData={currentStepData}
            selection={selection}
            owner={owner}
            hiddenSteps={hiddenSteps}
            onSelect={selectOption}
            onBack={handleBack}
            onReset={handleReset}
          />
        )}
      </div>
    </div>
  );
};

export default PreFormWizardWithSubCategory;
